import { take, put, call, fork, setContext, getContext, select } from 'redux-saga/effects';
import { eventChannel, EventChannel, SagaIterator } from 'redux-saga';
import Keycloak, { KeycloakInstance, KeycloakTokenParsed } from 'keycloak-js';

import { respawn } from 'yeself-client-common/utils/SagaUtils';

import {
    authInitSuccess,
    authInitFail,
    authSetAccountUrl,
    authSetUserProfile,
    authBusLogoutRequest,
    authBusLoginRequest,
    authSetUserRoles,
} from './SecurityAction';

import { selectIsLoginRequired, selectIsLoginFromIFrameEnabled } from './SecuritySelector';

import {
    createAccountUrl,
    setRequestAuth,
    loadUserProfile,
    logout,
    init,
    login,
    postMessageToAuthIframe,
    loadUserInfo,
} from './SecurityService';

import {
    AUTH_INIT_REQUEST,
    AUTH_LOGOUT_REQUEST,
    AUTH_INIT_SUCCESS,
    SecurityProfile,
    AUTH_BUS_SUCCESS,
    SecurityTokenPayload,
    SecurityOptions,
    AUTH_BUS_FAIL,
    AUTH_LOGIN_REQUEST,
} from './types';

const messageListener = (type): EventChannel<MessageEvent['data']> =>
    eventChannel((emitter) => {
        const callback = (e: MessageEvent): void => {
            if (e.data.type === type) {
                emitter(e.data);
            }
        };

        window.addEventListener('message', callback);

        return (): void => window.removeEventListener('message', callback);
    });

function* authBusSuccessTask({
    token,
    refreshToken,
    idToken,
}: SecurityTokenPayload): SagaIterator<void> {
    const keycloak: KeycloakInstance = yield getContext('securityAdapter');

    try {
        yield call(init, keycloak, {
            onLoad: 'check-sso',
            flow: 'hybrid',
            token,
            refreshToken,
            idToken,
            adapter: 'default',
        });

        if (keycloak.authenticated) {
            yield put(authInitSuccess());
        } else {
            yield put(authInitFail());
        }
    } catch (err) {
        yield put(authInitFail());
    }
}

function* authBusSuccessWatcher(): SagaIterator<never> {
    const chan = yield call(messageListener, AUTH_BUS_SUCCESS);

    while (true) {
        const { payload } = yield take(chan);
        yield fork(authBusSuccessTask, payload);
    }
}

function* authBusFailWatcher(): SagaIterator<never> {
    const chan = yield call(messageListener, AUTH_BUS_FAIL);

    while (true) {
        yield take(chan);
        yield put(authInitFail());
    }
}

function* authInitRequestTask(): SagaIterator<void> {
    const isLoginFromIFrameEnabled: boolean = yield select(selectIsLoginFromIFrameEnabled);

    if (!isLoginFromIFrameEnabled) {
        const keycloak: KeycloakInstance = yield getContext('securityAdapter');

        const isLoginRequired: boolean = yield select(selectIsLoginRequired);

        try {
            yield call(init, keycloak, {
                flow: 'hybrid',
                onLoad: isLoginRequired ? 'login-required' : 'check-sso',
            });

            if (keycloak.authenticated) {
                yield put(authInitSuccess());
            } else {
                yield put(authInitFail());
            }
        } catch (err) {
            yield put(authInitFail());
        }
    }
}

function* authInitRequestWatcher(): SagaIterator<never> {
    while (true) {
        yield take(AUTH_INIT_REQUEST);
        yield fork(authInitRequestTask);
    }
}

function* authLogoutRequestTask(): SagaIterator<void> {
    const isLoginFromIFrameEnabled: boolean = yield select(selectIsLoginFromIFrameEnabled);

    if (!isLoginFromIFrameEnabled) {
        const keycloak: KeycloakInstance = yield getContext('securityAdapter');

        yield call(logout, keycloak);
    } else {
        yield call(postMessageToAuthIframe, authBusLogoutRequest());
    }
}

function* authLogoutRequestWatcher(): SagaIterator<never> {
    while (true) {
        yield take(AUTH_LOGOUT_REQUEST);
        yield fork(authLogoutRequestTask);
    }
}

function* authLoginRequestTask(): SagaIterator<void> {
    const isLoginFromIFrameEnabled: boolean = yield select(selectIsLoginFromIFrameEnabled);

    if (!isLoginFromIFrameEnabled) {
        const keycloak: KeycloakInstance = yield getContext('securityAdapter');

        yield call(login, keycloak);
    } else {
        yield call(postMessageToAuthIframe, authBusLoginRequest());
    }
}

function* authLoginRequestWatcher(): SagaIterator<never> {
    while (true) {
        yield take(AUTH_LOGIN_REQUEST);
        yield fork(authLoginRequestTask);
    }
}

function* authInitSuccessTask(): SagaIterator<void> {
    const keycloak: KeycloakInstance = yield getContext('securityAdapter');

    const accountUrl: string = yield call(createAccountUrl, keycloak);
    const profile: SecurityProfile = yield call(loadUserProfile, keycloak);
    const { featureFlags } = yield call(loadUserInfo, keycloak);

    const roles = keycloak.realmAccess ? keycloak.realmAccess.roles : [];
    const groups = keycloak.tokenParsed
        ? (keycloak.tokenParsed as KeycloakTokenParsed & { groups: string[] }).groups
        : [];

    yield put(authSetAccountUrl(accountUrl));
    yield put(authSetUserProfile({ ...profile, groups, featureFlags}));
    yield put(authSetUserRoles(roles));
}

function* authInitSuccessWatcher(): SagaIterator<never> {
    while (true) {
        yield take(AUTH_INIT_SUCCESS);
        yield fork(authInitSuccessTask);
    }
}

function* SecuritySaga(options: SecurityOptions) {
    const keycloak: KeycloakInstance = yield call(Keycloak, {
        url: options.url,
        realm: options.realm,
        clientId: options.clientId,
    });

    yield setContext({ securityAdapter: keycloak });

    yield respawn(authInitRequestWatcher);
    yield respawn(authLoginRequestWatcher);
    yield respawn(authLogoutRequestWatcher);
    yield respawn(authInitSuccessWatcher);

    yield respawn(authBusSuccessWatcher);
    yield respawn(authBusFailWatcher);

    yield call(setRequestAuth, keycloak);
}

export default SecuritySaga;
